import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { formatGP, STATUS_COLOR } from "@/lib/format";
import { PageHead } from "@/pages/admin/AdminLayout";

function Stat({ label, value, id, hot }) {
  return (
    <div className="border border-[#2a2a2a] bg-[#0a0a0a] p-4 relative overflow-hidden" data-testid={id}>
      <div className="absolute top-0 left-0 w-8 h-[2px] bg-[#ff2a55]" />
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#777]">{label}</div>
      <div className={`font-display text-4xl mt-1 ${hot ? "hot-text" : ""}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { data: s } = useQuery({ queryKey: ["admin-stats"], queryFn: () => api.get("/admin/stats").then((r) => r.data), refetchInterval: 15000 });
  if (!s) return <div className="text-[#777]">Loading telemetry...</div>;
  return (
    <div data-testid="admin-dashboard">
      <PageHead title="Overview" sub="// mission telemetry" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat id="stat-revenue" label="Confirmed revenue" value={`${formatGP(s.revenue)} GP`} hot />
        <Stat id="stat-pending" label="Pending value" value={`${formatGP(s.pending_value)} GP`} />
        <Stat id="stat-orders" label="Total orders" value={s.orders} />
        <Stat id="stat-bounties" label="New bounties" value={s.new_requests} hot />
        <Stat id="stat-listings" label="Live listings" value={`${s.enabled_products} / ${s.products}`} />
        <Stat id="stat-users" label="Users" value={s.users} />
        <Stat id="stat-delivered" label="Delivered" value={s.by_status.delivered || 0} />
        <Stat id="stat-cancelled" label="Cancelled" value={s.by_status.cancelled || 0} />
      </div>
      <div className="grid lg:grid-cols-[2fr_1fr] gap-3 mt-3">
        <div className="border border-[#2a2a2a] bg-[#0a0a0a] p-4">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#777] mb-3">Revenue / 14 days (M GP)</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={s.series}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#ff2a55" stopOpacity={0.6} /><stop offset="100%" stopColor="#ff2a55" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#1e1e1e" />
                <XAxis dataKey="day" stroke="#555" fontSize={11} /><YAxis stroke="#555" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #ff2a55" }} />
                <Area type="monotone" dataKey="revenue" stroke="#ff2a55" fill="url(#g)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-[#2a2a2a] bg-[#0a0a0a] p-4 space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#777] mb-3">Orders by status</div>
            {Object.keys(STATUS_COLOR).slice(0, 5).map((k) => (
              <div key={k} className="flex justify-between text-sm py-1 border-b border-[#1c1c1c]"><span className="uppercase tracking-wider text-xs" style={{ color: STATUS_COLOR[k] }}>{k}</span><span>{s.by_status[k] || 0}</span></div>
            ))}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#777] mb-2">Top sellers</div>
            {s.top.length === 0 && <div className="text-xs text-[#555]">No sales yet</div>}
            {s.top.map((t) => <div key={t.title} className="flex justify-between text-sm py-1"><span>{t.title}</span><span className="text-[#ff2a55]">x{t.qty}</span></div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
